import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PageLayout } from '../components/layout/PageLayout';
import { useSlipPeriods, useSlip, useMyPayslips } from '../hooks';
import { apiFetch } from '../lib/api-client';
import { Download } from 'lucide-react';
import { useToast } from '../components/ui/toast';
import { openWhenReady } from '../lib/open-when-ready';

const fmtPlain = (n: number) => n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface PeriodRow { period: string; status: string; sent_at: string | null; }
interface SlipLine { label: string; amount: number; }
interface SlipData {
  period: string; gross: number; net: number;
  base_pay: number; ot_1_pay: number; ot_2_pay: number; ot_3_pay: number;
  additions_total: number;
  tax_amount: number; sso_amount: number; pf_amount: number;
  studentloan_amount: number; companyloan_amount: number;
  unpaid_leave_deduct: number; absent_deduct: number; manual_deductions: number;
  deductions_total: number;
  calculation_log?: { line_items?: { earnings?: SlipLine[]; deductions?: SlipLine[]; earnings_total?: number; deductions_total?: number }; ytd?: { gross: number; tax: number; sso: number; pf: number; net: number } } | null;
}

function fmt(n: number): string {
  return new Intl.NumberFormat('th-TH', { style: 'currency', currency: 'THB' }).format(n);
}

export function Slip() {
  const [period, setPeriod] = useState<string | null>(null);
  const { data: periods } = useSlipPeriods() as { data: PeriodRow[] | undefined };
  const { data: slip } = useSlip(period) as { data: SlipData | undefined };
  const { data: payslipData } = useMyPayslips();
  const payslips = payslipData?.payslips ?? [];
  const toast = useToast();

  const [payslipPdf, setPayslipPdf] = useState<string | null>(null);
  async function openPayslip(p: { period: string; releasable: boolean }) {
    if (!p.releasable) {
      toast.error('สลิปงวดนี้ยังเป็นพรีวิว — โหลดได้เมื่อจ่ายค่าคอมงวดนี้ หรือ HR อนุมัติออกสลิป');
      return;
    }
    if (payslipPdf) return;
    setPayslipPdf(p.period);
    try {
      await openWhenReady(async () => (await apiFetch<{ pdf_url: string }>(`/me/payslips/${p.period}/slip-pdf`)).pdf_url);
    } catch {
      toast.error('สร้าง PDF ไม่สำเร็จ ลองใหม่อีกครั้ง');
    } finally {
      setPayslipPdf(null);
    }
  }

  const [pdfLoading, setPdfLoading] = useState(false);
  async function downloadPdf() {
    if (!period || pdfLoading) return;
    setPdfLoading(true);
    try {
      await openWhenReady(async () => (await apiFetch<{ pdf_url: string }>(`/slip/pdf?period=${period}`)).pdf_url);
    } catch {
      toast.error('สร้าง PDF ไม่สำเร็จ ลองใหม่อีกครั้ง');
    } finally {
      setPdfLoading(false);
    }
  }

  if (!period) {
    return (
      <PageLayout title="สลิปเงินเดือน">
        <div className="space-y-2">
          {payslips.map((p) => (
            <button key={p.period} onClick={() => void openPayslip(p)} disabled={payslipPdf === p.period} className="card p-4 w-full text-left hover:bg-gray-50 flex items-center justify-between gap-2 disabled:opacity-50">
              <div className="min-w-0">
                <p className="font-medium truncate">เงินเดือนงวด {p.period_label}</p>
                <p className="text-xs text-gray-500">สุทธิ ฿{fmtPlain(p.net)}{!p.commission_paid && p.installments.some((i) => i.kind === 'commission') ? ' (ยังไม่รวมค่าคอม)' : ''}</p>
              </div>
              {p.releasable ? (
                <span className="shrink-0 inline-flex items-center gap-1 text-[12px] text-brand-600"><Download className="w-4 h-4" /> โหลด</span>
              ) : (
                <span className="shrink-0 text-[11px] text-amber-600 bg-amber-50 rounded-full px-2 py-1">พรีวิว · รอค่าคอม</span>
              )}
            </button>
          ))}
          {(periods ?? []).map((p) => (
            <button key={p.period} onClick={() => setPeriod(p.period)} className="card p-4 w-full text-left hover:bg-gray-50">
              <p className="font-medium">{p.period}</p>
              <p className="text-xs text-gray-500">{p.status === 'sent' ? 'ส่งแล้ว' : 'ปิดรอบแล้ว'}</p>
            </button>
          ))}
          {(periods ?? []).length === 0 && payslips.length === 0 && <p className="text-gray-500">ยังไม่มีสลิป</p>}
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title={`สลิป ${period}`}>
      <button onClick={() => setPeriod(null)} className="text-sm text-brand-600 mb-3">← เลือกรอบอื่น</button>
      {!slip ? <p>กำลังโหลด...</p> : (() => {
        const li = slip.calculation_log?.line_items;
        const earnings: SlipLine[] = li?.earnings ?? [
          { label: 'เงินเดือน / ค่าจ้าง', amount: (slip.base_pay ?? 0) + (slip.unpaid_leave_deduct ?? 0) + (slip.absent_deduct ?? 0) },
          { label: 'ค่าล่วงเวลา (OT1)', amount: slip.ot_1_pay },
          { label: 'ค่าล่วงเวลา (OT2)', amount: slip.ot_2_pay },
          { label: 'ค่าล่วงเวลา (OT3)', amount: slip.ot_3_pay },
          { label: 'เงินเพิ่ม / เบี้ยเลี้ยง', amount: slip.additions_total },
        ].filter((e) => e.amount !== 0);
        const deductions: SlipLine[] = li?.deductions ?? [
          { label: 'หักลาไม่รับเงิน', amount: slip.unpaid_leave_deduct },
          { label: 'หักขาดงาน', amount: slip.absent_deduct },
          { label: 'ภาษีหัก ณ ที่จ่าย', amount: slip.tax_amount },
          { label: 'ประกันสังคม', amount: slip.sso_amount },
          { label: 'กองทุนสำรองเลี้ยงชีพ', amount: slip.pf_amount },
          { label: 'กยศ. / กรอ.', amount: slip.studentloan_amount },
          { label: 'เงินกู้บริษัท', amount: slip.companyloan_amount },
          { label: 'รายการหักอื่น ๆ', amount: slip.manual_deductions },
        ].filter((d) => d.amount !== 0);
        // #13 — tie each total to the array actually rendered: only trust the
        // stored *_total when the matching itemized array is present (it includes
        // §76 carry/credit lines that the scalar fallback array never has).
        const earnTotal = li?.earnings ? (li.earnings_total ?? earnings.reduce((t, e) => t + e.amount, 0)) : earnings.reduce((t, e) => t + e.amount, 0);
        const dedTotal = li?.deductions ? (li.deductions_total ?? deductions.reduce((t, e) => t + e.amount, 0)) : deductions.reduce((t, e) => t + e.amount, 0);
        return (
        <div className="card p-4 space-y-3">
          <div>
            <p className="text-sm font-medium mb-1 text-green-700">รายได้</p>
            <div className="text-sm space-y-1 ml-2 text-gray-700">
              {earnings.map((e, i) => <div key={i} className="flex justify-between"><span>{e.label}</span><span>{fmt(e.amount)}</span></div>)}
              {earnings.length === 0 && <div className="text-gray-400">— ไม่มี —</div>}
            </div>
            <div className="flex justify-between border-t mt-1 pt-1 font-semibold"><span>รวมรายได้</span><span>{fmt(earnTotal)}</span></div>
          </div>
          <div>
            <p className="text-sm font-medium mb-1 text-red-600">รายการหัก</p>
            <div className="text-sm space-y-1 ml-2 text-gray-700">
              {deductions.map((d, i) => <div key={i} className="flex justify-between"><span>{d.label}</span><span>{d.amount < 0 ? `+${fmt(-d.amount)}` : `-${fmt(d.amount)}`}</span></div>)}
              {deductions.length === 0 && <div className="text-gray-400">— ไม่มี —</div>}
            </div>
            <div className="flex justify-between border-t mt-1 pt-1 font-semibold"><span>รวมรายการหัก</span><span>-{fmt(dedTotal)}</span></div>
          </div>
          <div className="flex justify-between border-t pt-2 text-lg font-bold text-brand-700">
            <span>เงินสุทธิ</span><span>{fmt(slip.net)}</span>
          </div>
          {slip.calculation_log?.ytd && (
            <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 text-xs text-gray-600 space-y-1">
              <p className="font-medium text-gray-500">ยอดสะสมตั้งแต่ต้นปี (YTD)</p>
              <div className="flex justify-between"><span>รายได้สะสม</span><span>{fmt(slip.calculation_log.ytd.gross)}</span></div>
              <div className="flex justify-between"><span>ภาษีสะสม</span><span>{fmt(slip.calculation_log.ytd.tax)}</span></div>
              <div className="flex justify-between"><span>ประกันสังคมสะสม</span><span>{fmt(slip.calculation_log.ytd.sso)}</span></div>
              <div className="flex justify-between"><span>กองทุนสำรองสะสม</span><span>{fmt(slip.calculation_log.ytd.pf)}</span></div>
              <div className="flex justify-between font-medium text-gray-700"><span>สุทธิสะสม</span><span>{fmt(slip.calculation_log.ytd.net)}</span></div>
            </div>
          )}
          {/* Conservative-withholding notice — ระบบหักภาษีเฉพาะลดหย่อนพื้นฐาน
              (60k + ค่าใช้จ่าย + SSO + PF). ผู้มีลดหย่อนเพิ่มต้องขอคืนตอนยื่น ภงด.91 */}
          {slip.tax_amount > 0 && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
              <p className="font-medium mb-0.5">ทำไมหักภาษีเท่านี้?</p>
              <p className="leading-relaxed">
                ระบบหักภาษีโดยคำนวณจากลดหย่อนพื้นฐาน
                (ส่วนตัว 60,000 + ค่าใช้จ่าย + ปกส. + PF) เท่านั้น —
                <strong> ยังไม่รวม</strong> คู่สมรส/บุตร/ประกัน/RMF/SSF/ดอกเบี้ยบ้าน.
                หากมีรายการเหล่านี้ คุณสามารถ <strong>ขอคืนตอนยื่น ภงด.91</strong> ปลายปีได้
              </p>
              <Link to="/factsheet#tax" className="inline-block mt-1 underline text-amber-700 hover:text-amber-900">
                ดูรายละเอียดวิธีคิดภาษี →
              </Link>
            </div>
          )}
          <button onClick={downloadPdf} disabled={pdfLoading} className="btn-cta w-full">
            <Download className="w-4 h-4" /> {pdfLoading ? 'กำลังสร้าง PDF…' : 'ดาวน์โหลด PDF'}
          </button>
          <div className="text-xs text-center text-gray-500 space-x-3">
            <Link to="/factsheet#tax" className="hover:text-brand-700 underline">ภาษี</Link>
            <Link to="/factsheet#sso" className="hover:text-brand-700 underline">ประกันสังคม</Link>
            <Link to="/factsheet#pf" className="hover:text-brand-700 underline">กองทุน</Link>
            <Link to="/factsheet#ytd" className="hover:text-brand-700 underline">YTD</Link>
          </div>
        </div>
        );
      })()}
    </PageLayout>
  );
}
